import { normalizeDueRule, computeDeadline, stageEntryPatch, isOpenTask } from "./listingPrep";
import { CONTRACT_STAGES, seedOwner } from "./taskOwners";
import { isClosedStatus } from "./taskStatus";
import { inScopeDeal } from "./checklistScope";
import { isTestFile } from "./testFiles";

// Canonical deal-stage order (mirrors the Task Template editor's STATUSES).
export const STAGE_ORDER = [
  "Prospect",
  "On Hold",
  "CMAs",
  "Listing Appointment",
  "Pre-Market",
  "Coming Soon",
  "Active",
  "Under Contract",
  "Pending",
  "Closed",
  "Cancelled",
  "Expired",
];

// An item is a section header only when type === "section".
// Anything else (including legacy items with no `type`) is a task.
export const isSectionItem = (item) => item?.type === "section";

// ---- Buyer files vs listing tasks -----------------------------------------
// Henry, 2026-09-10: "The tasks for buyer transactions have listing tasks
// added to it." The one Task Template is his LISTING checklist keyed by stage,
// and a buyer turn-in lands at Under Contract, so every stage before it was
// being copied onto buyer files. A buyer file never passes through these:
export const LISTING_ONLY_STAGES = [
  "Prospect",
  "On Hold",
  "CMAs",
  "Listing Appointment",
  "Pre-Market",
  "Coming Soon",
  "Active",
];

export const isBuyerSide = (dealtype) =>
  String(dealtype || "").trim().toLowerCase() === "buyer";

export const stageAppliesToSide = (stage, dealtype) =>
  !(isBuyerSide(dealtype) && LISTING_ONLY_STAGES.includes(stage));

// The stages whose template tasks a deal should receive when it lands at
// dealStage: everything up to and including it, minus the listing-only
// stages for a buyer file. Unknown stage = nothing.
export const stagesToSeed = (dealtype, dealStage, order = STAGE_ORDER) => {
  const idx = order.indexOf(dealStage);
  if (idx < 0) return [];
  return order.slice(0, idx + 1).filter((s) => stageAppliesToSide(s, dealtype));
};

// Rows in the shared later stages are prefixed by side in Henry's checklist
// ("Seller- Status change in MLS", "LC: Sellers- Pick up lockbox...").
const SELLER_TASK_RE = /^\s*(LC:\s*)?sellers?\s*-/i;
const BUYER_TASK_RE = /^\s*(LC:\s*)?buyers?\s*-/i;

export const taskAppliesToSide = (item, dealtype) => {
  const title = String(item?.title || "");
  if (isBuyerSide(dealtype)) return !SELLER_TASK_RE.test(title);
  if (String(dealtype || "").trim().toLowerCase() === "seller") return !BUYER_TASK_RE.test(title);
  return true;
};

export const hasTemplateContent = (template) =>
  Object.values(template || {}).some(
    (arr) => Array.isArray(arr) && arr.some((it) => it && !isSectionItem(it)),
  );

// Which template a deal seeds from: the Buyer template once it has any tasks,
// otherwise the listing template (the side filters above still apply to it).
export const templateForSide = ({ listing, buyer } = {}, dealtype) =>
  isBuyerSide(dealtype) && hasTemplateContent(buyer) ? buyer : listing || {};

// A starting point for the Buyer template: the listing template's shared
// later stages with the "Seller-" rows removed. Sections are kept.
export const buyerStarterFromListing = (listing = {}) => {
  const out = {};
  STAGE_ORDER.forEach((stage) => {
    if (LISTING_ONLY_STAGES.includes(stage)) return;
    out[stage] = (Array.isArray(listing[stage]) ? listing[stage] : []).filter(
      (it) => it && (isSectionItem(it) || taskAppliesToSide(it, "Buyer")),
    );
  });
  return out;
};

const cleanTaskItem = (t) => ({
  type: "task",
  id: t.id,
  title: t.title || "",
  status: "Open", // template tasks are always stored Open
  deadline: t.deadline ? String(t.deadline) : "",
  attachments: Array.isArray(t.attachments) ? t.attachments : [],
  assignee:
    t.assignee && t.assignee.id
      ? {
          id: t.assignee.id,
          firstname: t.assignee.firstname || "",
          lastname: t.assignee.lastname || "",
          profilepicture: t.assignee.profilepicture || "",
          role: t.assignee.role || "",
        }
      : null,
  createdAt: t.createdAt ? new Date(t.createdAt).toISOString() : null,
  coordinatorRole: !!t.coordinatorRole,
  transactionCoordinatorRole: !!t.transactionCoordinatorRole,
  // Listing Prep (spec 2026-09-25, section 10): the row's due rule and Required switch.
  dueRule: normalizeDueRule(t.dueRule),
  requiredBeforeActive: t.requiredBeforeActive === true,
  // Work tab (Plan B, spec section 7): the row's Work job kind, "" when the row is
  // not a job. expandStageItems spreads the row, so it rides onto the deal task.
  workKind: t.workKind ? String(t.workKind) : "",
});

// Clean one stage's items for saving: keep section items, normalize tasks.
export const cleanTemplateItems = (items = []) =>
  items.map((it) =>
    isSectionItem(it)
      ? { type: "section", id: it.id, title: it.title || "" }
      : cleanTaskItem(it),
  );

// Clean the whole template object (every stage).
export const cleanTemplate = (template = {}) =>
  Object.keys(template).reduce((acc, stage) => {
    acc[stage] = cleanTemplateItems(template[stage] || []);
    return acc;
  }, {});

// Turn a stage's template items into deal-ready task objects:
// drop header rows, and stamp each task with the nearest section above it
// (null if none) plus the stage. Order is preserved.
// Listing Prep (spec 2026-09-25, sections 3 and 4): a row on any file that is not a buyer
// file that no flag and no person already claims goes to the LC (`coordinator`), and every
// task gets its due date from the row's rule when the caller passes the deal and today. Old
// callers that pass neither get no date (never a wrong one). `today` is the viewer's day;
// server code must pass the Chicago day.
// My Checklist (spec 09/27/2026, decision 4): on a buyer file every row goes to the Client Care
// person (`clientCare`, from coordinatorsFor; LC flags on buyer rows are listing jobs by mistake),
// and on a listing at Under Contract or later unflagged and TC-flagged rows go to Client Care while
// LC-flagged rows stay with the LC. Prep stages and Active are unchanged. Callers that pass no
// clientCare fall back to the transactionCoordinator.
// Henry 10/08/2026: the owner rule itself lives in utils/taskOwners.js (seedOwner), the same rule
// Settings > Coordinators uses to re-point items already on files, so the two never disagree.
export { CONTRACT_STAGES };
export const expandStageItems = (
  items = [],
  stage = null,
  { coordinator = null, transactionCoordinator = null, clientCare, dealtype = null, deal = null, today = null } = {},
) => {
  const owners = { coordinator, transactionCoordinator, clientCare };
  // A test file (utils/testFiles.js, Henry 10/06/2026: "Nobody has assigned tasks from this") hands nobody
  // work: every row starts with no owner, so no "New Task Assigned" alert ever goes out for it.
  const testFile = isTestFile(deal);
  if (!Array.isArray(items)) return [];
  let currentSection = null;
  const out = [];
  for (const it of items) {
    if (isSectionItem(it)) {
      currentSection = it.title || null;
      continue;
    }
    // Given the deal's side, leave the other side's rows behind.
    if (dealtype && !taskAppliesToSide(it, dealtype)) continue;
    const base = { ...it, stage, section: currentSection };
    if (it.coordinatorRole) base.coordinatorRole = true;
    if (it.transactionCoordinatorRole) base.transactionCoordinatorRole = true;
    // A person Henry named on the row keeps it; an LC row on a listing stays with the LC
    // (taskOwners.seedOwner).
    Object.assign(base, seedOwner(it, stage, dealtype, owners));
    if (!base.assignee) base.assignee = null;
    if (testFile) {
      base.assignee = null;
      base.routedToLC = false;
    }
    base.dueRule = normalizeDueRule(it.dueRule);
    base.requiredBeforeActive = it.requiredBeforeActive === true;
    base.deadline = deal || today ? computeDeadline(base.dueRule, { stage, deal: deal || {}, today }) : "";
    out.push(base);
  }
  return out;
};

// Group a stage's template items into ordered sections for the editor.
// Returns [{ section: {id,title} | null, tasks: [...] }].
export const groupTemplateItems = (items = []) => {
  const groups = [];
  let current = { section: null, tasks: [] };
  for (const it of items) {
    if (isSectionItem(it)) {
      if (current.section || current.tasks.length) groups.push(current);
      current = { section: { id: it.id, title: it.title || "" }, tasks: [] };
    } else {
      current.tasks.push(it);
    }
  }
  if (current.section || current.tasks.length) groups.push(current);
  return groups;
};

// done/total for a list of tasks (done = Completed or N/A). N/A counts as done here (10/06/2026) so a
// section whose items are all done or not applicable turns green, and a pill never reads 0/0.
export const sectionProgress = (tasks = []) => ({
  done: tasks.filter((t) => t && isClosedStatus(t.status)).length,
  total: tasks.length,
});

// Group a deal's flat tasks by stage (in stageOrder; unknown stages last)
// then by section (first-seen order). Returns
// [{ stage, sections: [{ title|null, tasks, done, total }] }].
export const groupDealTasks = (tasks = [], stageOrder = []) => {
  const byStage = new Map();
  for (const t of tasks) {
    const stage = t.stage || "Other";
    if (!byStage.has(stage)) byStage.set(stage, []);
    byStage.get(stage).push(t);
  }
  const known = stageOrder.filter((s) => byStage.has(s));
  const unknown = [...byStage.keys()].filter((s) => !stageOrder.includes(s));
  return [...known, ...unknown].map((stage) => {
    const stageTasks = byStage.get(stage);
    const order = [];
    const bySection = new Map();
    for (const t of stageTasks) {
      const key = t.section || "";
      if (!bySection.has(key)) {
        bySection.set(key, []);
        order.push(key);
      }
      bySection.get(key).push(t);
    }
    const sections = order.map((key) => {
      const secTasks = bySection.get(key);
      const { done, total } = sectionProgress(secTasks);
      return { title: key || null, tasks: secTasks, done, total };
    });
    return { stage, sections };
  });
};

// Active stages: STAGE_ORDER minus the finished ones.
export const ACTIVE_STAGES = STAGE_ORDER.filter(
  (s) => !["Closed", "Cancelled", "Expired"].includes(s),
);

// Build new tasks + tracker to backfill one deal from the template.
// Adds tasks for every stage up to & including deal.dealStage that the deal does
// NOT already have tasks for. Current stage -> "Open", earlier -> "Completed".
// Coordinator-role tasks resolve to `coordinator`. Existing deal.tasks are kept.
// NOTE: "already has this stage" is keyed off the deal's ACTUAL tasks, not
// taskStatusTracker — deals created while the template was empty have stages
// marked "applied" in the tracker yet hold no tasks, so trusting it would skip
// exactly the deals we need to backfill.
// Returns { tasks, taskStatusTracker } or null when there's nothing to add or the
// stage is unknown.
export const buildDealBackfill = (deal, template = {}, { coordinator = null, transactionCoordinator = null, clientCare, stageOrder = STAGE_ORDER, nowIso = null, today = null } = {}) => {
  const current = deal?.dealStage;
  const idx = stageOrder.indexOf(current);
  if (idx < 0) return null;
  const allowed = stageOrder.slice(0, idx + 1);
  const existingStages = new Set((deal.tasks || []).map((t) => t.stage).filter(Boolean));
  const tracker = { ...(deal.taskStatusTracker || {}) };
  const added = [];
  for (const stage of allowed) {
    // A buyer file never passes through the listing stages; mark them applied
    // so a later stage move can't back-fill them either (Henry, 2026-09-10).
    if (!stageAppliesToSide(stage, deal?.dealtype)) {
      tracker[stage] = true;
      continue;
    }
    if (existingStages.has(stage)) continue;
    const stageTasks = expandStageItems(template[stage] || [], stage, { coordinator, transactionCoordinator, clientCare, dealtype: deal?.dealtype, deal, today }).map((t) => ({
      ...t,
      status: stage === current ? "Open" : "Completed",
      createdAt: nowIso,
    }));
    if (stageTasks.length === 0) continue;
    added.push(...stageTasks);
    tracker[stage] = true;
  }
  if (added.length === 0) return null;
  return { tasks: [...(deal.tasks || []), ...added], taskStatusTracker: tracker };
};

// The items a single stage move adds (My Checklist spec 09/27/2026, decision 13): the deal-page
// stage pill and a turn-in that MOVES an on-board listing seed only the stage the file lands on,
// Open, with owners (coordinatorsFor) and dates from the row rules. Nothing when the stage is
// listing-only on a buyer file, already applied (taskStatusTracker) or already on the deal.
export const newStageTasks = ({ deal, newStage, template = {}, owners = {}, today, nowIso = null }) => {
  if (!deal || !newStage || !stageAppliesToSide(newStage, deal.dealtype)) return [];
  if ((deal.taskStatusTracker || {})[newStage]) return [];
  if ((deal.tasks || []).some((t) => t && t.stage === newStage)) return [];
  const dated = { ...deal, ...stageEntryPatch(deal, deal.dealStage, newStage, today), dealStage: newStage };
  return expandStageItems((template || {})[newStage] || [], newStage, { ...owners, dealtype: deal.dealtype, deal: dated, today })
    .map((t) => ({ ...t, status: "Open", createdAt: nowIso }));
};

// A listing moved straight to Active from an earlier stage jumps over the stages in between, and
// their "Required before Active" rows were never put on the deal, so the Active gate had nothing
// to stop on. These are those rows: required only, Open, with owners and dates, from every
// jumped-over stage that is not applied yet (taskStatusTracker) and has nothing on the deal. The
// caller adds them BEFORE the gate reads the checklist. Nothing on a move that is not to Active,
// not forward, or on a buyer file.
export const skippedRequiredTasks = ({ deal, newStage, template = {}, owners = {}, today, nowIso = null, stageOrder = STAGE_ORDER }) => {
  if (!deal || newStage !== "Active" || isBuyerSide(deal.dealtype)) return [];
  const from = stageOrder.indexOf(deal.dealStage);
  const to = stageOrder.indexOf(newStage);
  if (from < 0 || to < 0 || to - from < 2) return [];
  const tracker = deal.taskStatusTracker || {};
  const onDeal = new Set((deal.tasks || []).map((t) => t && t.stage).filter(Boolean));
  const out = [];
  stageOrder.slice(from + 1, to).forEach((stage) => {
    // On Hold is a parking spot, not a step on the way to Active.
    if (stage === "On Hold" || tracker[stage] || onDeal.has(stage)) return;
    expandStageItems((template || {})[stage] || [], stage, { ...owners, dealtype: deal.dealtype, deal, today })
      .filter((t) => t.requiredBeforeActive === true)
      .forEach((t) => out.push({ ...t, status: "Open", createdAt: nowIso }));
  });
  return out;
};

// Dry run for backfillActiveDeals: how much a backfill would actually change,
// without writing anything. Same skip rules as the real thing, so the numbers
// the confirm dialog shows are the numbers that will happen.
// Returns { active, needing, missing } — active deals considered, how many of
// them would gain tasks, and how many tasks would be added in total.
export const previewDealBackfill = (deals = [], template = {}) => {
  let active = 0;
  let needing = 0;
  let missing = 0;
  for (const deal of deals || []) {
    if (deal?.archived || !ACTIVE_STAGES.includes(deal?.dealStage)) continue;
    active++;
    const result = buildDealBackfill(deal, template, {});
    if (!result) continue;
    needing++;
    missing += result.tasks.length - (deal.tasks || []).length;
  }
  return { active, needing, missing };
};

// Move Them (Settings > Coordinators). An LC job is a row flagged LC on the template or one the
// catch-all rule sent to the LC (routedToLC); both move when the agent's LC changes.
export const isLCTask = (t) => !!t && (t.coordinatorRole === true || t.routedToLC === true);
const openOn = (t, personId) =>
  !!t && !!personId && isOpenTask(t) && String((t.assignee && t.assignee.id) || "") === String(personId);
export const movesWithLC = (t, oldCoordId) => openOn(t, oldCoordId) && isLCTask(t);
// A Client Care job: anything the old person holds except a listing's LC jobs (on a buyer file the
// LC flag is a mistake and Client Care owns the row, spec 09/27/2026 decision 4).
export const movesWithClientCare = (t, oldId, deal) =>
  openOn(t, oldId) && (isBuyerSide(deal && deal.dealtype) || !isLCTask(t));

// Find an agent's still-open LC jobs currently on a given coordinator.
// Returns [{ dealId, task }]. dealId uses deal.createdat (the deal doc id).
export const findAgentCoordinatorTasks = (deals = [], agentId, oldCoordId) => {
  const out = [];
  for (const deal of deals) {
    if (deal?.agentData !== agentId) continue;
    for (const task of deal.tasks || []) {
      if (movesWithLC(task, oldCoordId)) out.push({ dealId: String(deal.createdat), task });
    }
  }
  return out;
};

// The same for a change of the agent's Client Care person, on live files only (checklistScope).
export const findAgentClientCareTasks = (deals = [], agentId, oldId, today) => {
  const out = [];
  for (const deal of deals || []) {
    if (deal?.agentData !== agentId || !inScopeDeal(deal, today)) continue;
    for (const task of deal.tasks || []) {
      if (movesWithClientCare(task, oldId, deal)) out.push({ dealId: String(deal.createdat), task });
    }
  }
  return out;
};
